import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { safeHref } from '../../../../utils/common/safe-url';
import {
  AdminBannedAccount,
  AdminBrandCancelRequest,
  AdminBrandConversion,
  AdminBrandDeal,
  AdminBrandDisputeDetail,
  AdminBrandDisputeListItem,
  AdminReputation,
  AdminReputationDetail,
  BrandCampaign,
  BrandRatingSubjectType,
  adminApproveCancelRequest,
  adminBanAccount,
  adminGetDispute,
  adminGetReputation,
  adminListBannedAccounts,
  adminListBrandCampaigns,
  adminListBrandConversions,
  adminListBrandDeals,
  adminListCancelRequests,
  adminListDisputes,
  adminListReputations,
  adminRejectCancelRequest,
  adminResolveBrandDeal,
  adminReviewBrandCampaign,
  adminSetReputationExempt,
  adminUnbanAccount,
  adminVoidBrandConversion,
} from '../../../../api/payment/brand-deal.api';
import { dealStatusLabel, listingStatusLabel, slotsLeft } from '../../../../utils/brand-deal';
import { countdownLabel, taskStatusLabel } from '../../../../utils/brand-creative';
import { DECISION_REASON_LIMITS, disputeDecidedToast, shouldOfferSanction, validateDecisionReason } from '../../../../utils/brand-dispute';
import { avgStarsLabel, RATING_EXEMPT_REASON_LIMITS, validateRatingExemptReason } from '../../../../utils/brand-rating';
import { LoaderCircle } from 'lucide-react';
import { AdminPageHeader, AdminPillTabs } from '../../ui';
import AdminEmpty from '../../components/AdminEmpty';
import { hasStrictPermission, isSuperAdmin, PERMISSION } from '../../../../utils/admin-permissions';
import AdminBookingReview from '../../../../components/brand-live/admin-booking-review';
import AdminLiveReports from '../../../../components/brand-live/admin-live-reports';
import AdminLiveCancelRequests from '../../../../components/brand-live/admin-live-cancel-requests';
import { adminListLiveCancelRequests, adminListLiveReports } from '../../../../api/payment/brand-live.api';
import BrandTaskReview from '../../../../components/brand-deal/brand-task-review';
import AdminFlaggedMessages from '../../../../components/brand-deal/admin-flagged-messages';
import BrandThreadPanel from '../../../../components/brand-deal/brand-thread-panel';
import AdminRatingHideDialog from '../../../../components/brand-deal/admin-rating-hide-dialog';
import { AdminOrdererLabel, ordererNoun } from '../../../../components/brand-deal/orderer-badge';
import AdminSanctionPanel from '../../../../components/brand-sanction/admin-sanction-panel';
import AdminBlacklistPanel from '../../../../components/brand-sanction/admin-blacklist-panel';
import AdminSanctionForm from '../../../../components/brand-sanction/admin-sanction-form';
import EditedAfterReviewBadge from '../../../../components/brand-deal/edited-after-review-badge';
import AdminDisputeDecideDialog from '../../../../components/brand-deal/admin-dispute-decide-dialog';

// Trang admin "Hợp tác creator" (Task 7) — đăng ký route/menu + gate quyền giống hệt ManagementAds
// (PERMISSION.AdsManage). Tab "Chờ duyệt" (chiến dịch) + "Chờ xem xét" (deal NeedsReview, cần mã
// Authenticator — step-up giống trang duyệt rút tiền ManagementWithdrawRequest).
//
// M3 (Task 7): thêm tab "Yêu cầu huỷ" (brief sáng tạo — doanh nghiệp xin huỷ nhiệm vụ đang chạy, admin
// đồng ý/không đồng ý, cùng cần mã Authenticator) + bấm 1 dòng mở "Xem nhiệm vụ" (BrandTaskReview,
// viewerRole="Admin" — chỉ xem: phiên bản, lịch sử soạn thảo, nhật ký xem, lịch sử quyết định).
//
// M4 (Task 4): thêm tab "Kháng cáo" (cũ nhất lên đầu, đánh dấu "Sẵn sàng quyết" — màn xét 3 cột bằng
// chứng creator | giải trình doanh nghiệp | bằng chứng M3 — dùng lại BrandTaskReview cho cột M3; hai nút
// quyết + lý do 10–1.000 ký tự + mã Authenticator) + tab "Doanh nghiệp bị khoá" (khoá/gỡ khoá thủ công,
// chỉ SuperAdmin — ẩn thao tác khi FE biết KHÔNG phải SuperAdmin, backend vẫn chặn thật + trả 403).

/** Yêu cầu huỷ của deal livestream (BE chưa có cờ riêng, deal livestream đang chạy có trạng thái Airing). */
const isLiveCancel = (r: AdminBrandCancelRequest) => (r.dealStatus as string) === 'Airing';

const NoAccess: React.FC = () => (
  <div className="p-6 text-gray-500 dark:text-gray-400">Bạn không có quyền truy cập khu vực này.</div>
);

const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

/** "Creator" / "Doanh nghiệp" — nhãn loại chủ thể điểm uy tín (tab "Uy tín", Task 4). */
const subjectTypeLabel = (type: BrandRatingSubjectType) => (type === 'Creator' ? 'Creator' : 'Doanh nghiệp');

/** Tên hiển thị của chủ thể điểm uy tín (Task 4 fix review): ưu tiên `subjectName` BE trả về
 * (BusinessName/tên creator); subject chưa có tên (đã xoá hoặc chưa có hồ sơ) rơi về id rút gọn, cùng mẫu
 * `creatorId.slice(0, 8)…` các tab khác trong trang này. */
const subjectName = (row: Pick<AdminReputation, 'subjectType' | 'subjectId' | 'subjectName'>) =>
  row.subjectName?.trim() || `${subjectTypeLabel(row.subjectType)} ${row.subjectId.slice(0, 8)}…`;

type ResolveModalState = {
  deal: AdminBrandDeal;
  payCreator: boolean;
  note: string;
  totp: string;
};

/** Modal đồng ý/không đồng ý 1 yêu cầu huỷ nhiệm vụ — cần mã Authenticator, cùng mẫu `ResolveModalState`. */
type CancelDecisionModalState = {
  request: AdminBrandCancelRequest;
  approve: boolean;
  note: string;
  totp: string;
};

const TABS = [
  { key: 'campaigns', label: 'Chờ duyệt' },
  { key: 'deals', label: 'Chờ xem xét' },
  { key: 'cancelRequests', label: 'Yêu cầu huỷ' },
  { key: 'liveReports', label: 'Báo gian lận livestream' },
  { key: 'liveCancels', label: 'Yêu cầu dừng livestream' },
  { key: 'disputes', label: 'Kháng cáo' },
  { key: 'bannedAccounts', label: 'Doanh nghiệp bị khoá' },
  { key: 'sanctions', label: 'Xử phạt người đặt đơn' },
  { key: 'blacklist', label: 'Danh sách đen' },
  { key: 'reputations', label: 'Uy tín' },
  { key: 'conversions', label: 'Chuyển đổi' },
  { key: 'flaggedMessages', label: 'Tin nhắn có cờ' },
];

type TabKey =
  | 'campaigns'
  | 'deals'
  | 'cancelRequests'
  | 'liveReports'
  | 'liveCancels'
  | 'disputes'
  | 'bannedAccounts'
  | 'sanctions'
  | 'blacklist'
  | 'reputations'
  | 'conversions'
  | 'flaggedMessages';

/** Modal huỷ 1 chuyển đổi (gian lận) — lý do 10–500 ký tự + mã Authenticator, cùng mẫu `ResolveModalState`. */
type VoidConversionModalState = {
  conversion: AdminBrandConversion;
  reason: string;
  totp: string;
};

/** Modal khoá tay 1 tài khoản doanh nghiệp — chỉ SuperAdmin. */
type ManualBanModalState = {
  adAccountId: string;
  reason: string;
  totp: string;
};

/** Modal gỡ khoá 1 tài khoản — chỉ cần mã Authenticator. */
type UnbanModalState = {
  account: AdminBannedAccount;
  totp: string;
};

/** Modal đặt/bỏ miễn trừ 1 hồ sơ điểm uy tín (Task 4, spec §2.3) — lý do 10–500 ký tự + mã Authenticator,
 * cùng mẫu `ResolveModalState`. `exempt` = giá trị MỚI sẽ đặt (true = miễn trừ, false = bỏ miễn trừ). */
type ReputationExemptModalState = {
  subjectType: BrandRatingSubjectType;
  subjectId: string;
  exempt: boolean;
  reason: string;
  totp: string;
};

const ManagementBrandDealsInner: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('campaigns');
  const [campaigns, setCampaigns] = useState<BrandCampaign[]>([]);
  const [deals, setDeals] = useState<AdminBrandDeal[]>([]);
  const [cancelRequests, setCancelRequests] = useState<AdminBrandCancelRequest[]>([]);
  const [disputes, setDisputes] = useState<AdminBrandDisputeListItem[]>([]);
  const [bannedAccounts, setBannedAccounts] = useState<AdminBannedAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [resolveModal, setResolveModal] = useState<ResolveModalState | null>(null);
  const [cancelModal, setCancelModal] = useState<CancelDecisionModalState | null>(null);
  // Số mục đang chờ của 2 tab livestream (tiền đang giữ đến khi admin quyết), hiện trên nhãn tab.
  const [liveReportCount, setLiveReportCount] = useState<number | null>(null);
  const [liveCancelCount, setLiveCancelCount] = useState<number | null>(null);
  const [inspectDealId, setInspectDealId] = useState<string | null>(null);
  const [threadDealId, setThreadDealId] = useState<string | null>(null);

  // M4 — kháng cáo: id đang xét (mở màn 3 cột) + chi tiết tải riêng (bằng chứng M3 nặng, chỉ tải khi mở).
  const [reviewDisputeId, setReviewDisputeId] = useState<string | null>(null);
  const [disputeDetail, setDisputeDetail] = useState<AdminBrandDisputeDetail | null>(null);
  const [disputeDetailLoading, setDisputeDetailLoading] = useState(false);
  const [decideOpen, setDecideOpen] = useState(false);
  // Bước sau khi quyết "Creator đúng": phạt người đặt đơn là lời gọi riêng, có mã Authenticator riêng (RB9).
  const [sanctionAfterDispute, setSanctionAfterDispute] = useState<{ adAccountId: string; disputeId: string } | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // M4 — doanh nghiệp bị khoá: khoá/gỡ khoá thủ công (chỉ SuperAdmin).
  const [manualBanModal, setManualBanModal] = useState<ManualBanModalState | null>(null);
  const [unbanModal, setUnbanModal] = useState<UnbanModalState | null>(null);

  // M5 (Task 4) — tab "Uy tín": danh sách hồ sơ điểm uy tín (thấp nhất lên đầu), lọc "Chỉ uy tín thấp"
  // (bật mặc định), xem chi tiết 1 hồ sơ (mọi lượt chấm, kể cả chưa công bố) + miễn trừ/bỏ miễn trừ.
  const [reputations, setReputations] = useState<AdminReputation[]>([]);
  const [lowOnlyReputations, setLowOnlyReputations] = useState(true);
  const [reputationDetailSubject, setReputationDetailSubject] = useState<{ subjectType: BrandRatingSubjectType; subjectId: string } | null>(null);
  const [reputationDetail, setReputationDetail] = useState<AdminReputationDetail | null>(null);
  const [reputationDetailLoading, setReputationDetailLoading] = useState(false);
  const [exemptModal, setExemptModal] = useState<ReputationExemptModalState | null>(null);
  // Ẩn/bỏ ẩn một lượt chấm vi phạm (spec 2026-10-12 §4) — lý do + mã Authenticator trong dialog riêng.
  const [hideTarget, setHideTarget] = useState<{ ratingId: string; mode: 'hide' | 'unhide' } | null>(null);

  // Đo chuyển đổi (spec 2026-10-05-brand-conversion §3) — tab "Chuyển đổi": nhập id chiến dịch, xem danh
  // sách (kể cả đã huỷ/thử), huỷ 1 chuyển đổi gian lận (lý do + Authenticator).
  const [conversionCampaignId, setConversionCampaignId] = useState('');
  const [conversions, setConversions] = useState<AdminBrandConversion[]>([]);
  const [conversionsPage, setConversionsPage] = useState(1);
  const [conversionsTotalPages, setConversionsTotalPages] = useState(0);
  const [conversionsLoaded, setConversionsLoaded] = useState(false);
  const [voidModal, setVoidModal] = useState<VoidConversionModalState | null>(null);

  useEffect(() => {
    adminListLiveReports('Open').then((l) => setLiveReportCount(l.length)).catch(() => undefined);
    adminListLiveCancelRequests('Pending').then((l) => setLiveCancelCount(l.length)).catch(() => undefined);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const load = async (t: TabKey) => {
    try {
      setLoading(true);
      if (t === 'campaigns') setCampaigns(await adminListBrandCampaigns('PendingReview'));
      else if (t === 'deals') setDeals(await adminListBrandDeals('NeedsReview'));
      else if (t === 'cancelRequests') setCancelRequests(await adminListCancelRequests('Pending'));
      else if (t === 'liveReports' || t === 'liveCancels') {
        // Hai tab livestream tự tải dữ liệu trong component riêng.
      }
      else if (t === 'disputes') setDisputes(await adminListDisputes('Open'));
      else if (t === 'reputations') setReputations(await adminListReputations(lowOnlyReputations));
      else if (t === 'conversions') {
        // Cần id chiến dịch trước (nhập tay, giống "Khoá thủ công") — không tự tải khi vừa mở tab.
      } else if (t === 'sanctions' || t === 'blacklist' || t === 'flaggedMessages') {
        // Hai tab xử phạt tự tải dữ liệu trong component riêng.
      } else setBannedAccounts(await adminListBannedAccounts());
    } catch (e) {
      toast.error(errorMessage(e, 'Không tải được danh sách.'));
    } finally {
      setLoading(false);
    }
  };

  /** Tải danh sách chuyển đổi của 1 chiến dịch (kể cả đã huỷ/thử) — gọi khi bấm "Xem chuyển đổi" hoặc
   * đổi trang, cũng như sau khi huỷ xong 1 chuyển đổi (tải lại đúng trang đang xem). */
  const loadConversions = async (campaignId: string, page: number) => {
    const id = campaignId.trim();
    if (!id) {
      toast.error('Cần nhập id chiến dịch.');
      return;
    }
    try {
      setLoading(true);
      const res = await adminListBrandConversions(id, page);
      setConversions(res.items);
      setConversionsPage(page);
      setConversionsTotalPages(res.totalPages);
      setConversionsLoaded(true);
    } catch (e) {
      toast.error(errorMessage(e, 'Không tải được danh sách chuyển đổi.'));
    } finally {
      setLoading(false);
    }
  };

  /** Final-review I3: "Xem chuyển đổi" trên từng dòng chiến dịch/deal — chuyển thẳng sang tab "Chuyển
   * đổi" với id đã điền sẵn, không bắt admin phải tự tra/gõ tay GUID (ô nhập tay vẫn còn, dùng khi không
   * có sẵn dòng nào để bấm, vd tra theo báo cáo gian lận nêu thẳng id). */
  const openConversionsForCampaign = (campaignId: string) => {
    setConversionCampaignId(campaignId);
    setTab('conversions');
    loadConversions(campaignId, 1);
  };

  const confirmVoidConversion = async () => {
    if (!voidModal) return;
    const { conversion, reason, totp } = voidModal;
    if (totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số.');
      return;
    }
    const trimmed = reason.trim();
    if (trimmed.length < 10 || trimmed.length > 500) {
      toast.error('Lý do huỷ cần 10–500 ký tự.');
      return;
    }
    try {
      setBusy(conversion.id);
      await adminVoidBrandConversion(conversion.id, totp, trimmed);
      toast.success('Đã huỷ chuyển đổi.');
      setVoidModal(null);
      loadConversions(conversionCampaignId, conversionsPage);
    } catch (e) {
      toast.error(errorMessage(e, 'Mã Authenticator không hợp lệ hoặc xử lý thất bại.'));
    } finally {
      setBusy(null);
    }
  };

  /** Tải lại danh sách "Uy tín" với `lowOnly` truyền trực tiếp — dùng khi bấm đổi filter (tránh đọc
   * state `lowOnlyReputations` cũ do `setState` bất đồng bộ) lẫn sau khi miễn trừ/bỏ miễn trừ. */
  const loadReputations = async (lowOnly: boolean) => {
    try {
      setLoading(true);
      setReputations(await adminListReputations(lowOnly));
    } catch (e) {
      toast.error(errorMessage(e, 'Không tải được danh sách.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!reviewDisputeId) {
      setDisputeDetail(null);
      return;
    }
    setDisputeDetailLoading(true);
    adminGetDispute(reviewDisputeId)
      .then(setDisputeDetail)
      .catch((e) => {
        toast.error(errorMessage(e, 'Không tải được kháng cáo.'));
        setReviewDisputeId(null);
      })
      .finally(() => setDisputeDetailLoading(false));
  }, [reviewDisputeId]);

  // M5 (Task 4) — chi tiết 1 hồ sơ điểm uy tín, tải riêng khi mở (giống `reviewDisputeId`/`disputeDetail`).
  useEffect(() => {
    if (!reputationDetailSubject) {
      setReputationDetail(null);
      return;
    }
    setReputationDetailLoading(true);
    adminGetReputation(reputationDetailSubject.subjectType, reputationDetailSubject.subjectId)
      .then(setReputationDetail)
      .catch((e) => {
        toast.error(errorMessage(e, 'Không tải được hồ sơ điểm uy tín.'));
        setReputationDetailSubject(null);
      })
      .finally(() => setReputationDetailLoading(false));
  }, [reputationDetailSubject]);

  const openExempt = (subjectType: BrandRatingSubjectType, subjectId: string, currentExempt: boolean) =>
    setExemptModal({ subjectType, subjectId, exempt: !currentExempt, reason: '', totp: '' });

  const confirmExempt = async () => {
    if (!exemptModal) return;
    const { subjectType, subjectId, exempt, reason, totp } = exemptModal;
    if (totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số.');
      return;
    }
    const reasonError = validateRatingExemptReason(reason);
    if (reasonError) {
      toast.error(reasonError);
      return;
    }
    const busyKey = `exempt-${subjectType}-${subjectId}`;
    try {
      setBusy(busyKey);
      await adminSetReputationExempt(subjectType, subjectId, totp, exempt, reason.trim());
      toast.success(exempt ? 'Đã miễn trừ.' : 'Đã bỏ miễn trừ.');
      setExemptModal(null);
      loadReputations(lowOnlyReputations);
      if (
        reputationDetailSubject &&
        reputationDetailSubject.subjectType === subjectType &&
        reputationDetailSubject.subjectId === subjectId
      ) {
        adminGetReputation(subjectType, subjectId)
          .then(setReputationDetail)
          .catch(() => undefined);
      }
    } catch (e) {
      toast.error(errorMessage(e, 'Miễn trừ thất bại.'));
    } finally {
      setBusy(null);
    }
  };

  // Quyết xong (spec 2026-10-10-brand-order-rules §4): đóng màn xét, mở bước "Xử phạt người đặt đơn" khi creator nhận từ 10%.
  const onDisputeDecided = (percent: number) => {
    const ordererAccountId = disputeDetail?.adAccountId ?? null;
    const disputeId = disputeDetail?.dispute.id ?? null;
    toast.success(disputeDecidedToast(percent));
    setDecideOpen(false);
    setReviewDisputeId(null);
    if (shouldOfferSanction(percent) && ordererAccountId && disputeId) setSanctionAfterDispute({ adAccountId: ordererAccountId, disputeId });
    else if (shouldOfferSanction(percent))
      toast('Chưa xác định được người đặt đơn của kháng cáo này. Hãy xử phạt ở tab "Xử phạt người đặt đơn" nếu cần.', { duration: 8000 });
    load('disputes');
  };

  const confirmManualBan = async () => {
    if (!manualBanModal) return;
    const { adAccountId, reason, totp } = manualBanModal;
    if (!adAccountId.trim()) {
      toast.error('Cần nhập id tài khoản doanh nghiệp.');
      return;
    }
    if (totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số.');
      return;
    }
    const reasonError = validateDecisionReason(reason);
    if (reasonError) {
      toast.error(reasonError);
      return;
    }
    try {
      setBusy('manual-ban');
      await adminBanAccount(adAccountId.trim(), totp, reason.trim());
      toast.success('Đã khoá tài khoản khỏi sàn hợp tác.');
      setManualBanModal(null);
      load('bannedAccounts');
    } catch (e) {
      toast.error(errorMessage(e, 'Khoá thất bại.'));
    } finally {
      setBusy(null);
    }
  };

  const confirmUnban = async () => {
    if (!unbanModal) return;
    const { account, totp } = unbanModal;
    if (totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số.');
      return;
    }
    try {
      setBusy(account.id);
      await adminUnbanAccount(account.id, totp);
      toast.success('Đã gỡ khoá tài khoản.');
      setUnbanModal(null);
      load('bannedAccounts');
    } catch (e) {
      toast.error(errorMessage(e, 'Gỡ khoá thất bại.'));
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const approveCampaign = async (c: BrandCampaign) => {
    try {
      setBusy(c.id);
      await adminReviewBrandCampaign(c.id, true);
      toast.success('Đã duyệt chiến dịch.');
      load('campaigns');
    } catch (e) {
      toast.error(errorMessage(e, 'Duyệt thất bại.'));
    } finally {
      setBusy(null);
    }
  };

  const rejectCampaign = async (c: BrandCampaign) => {
    const reason = window.prompt('Lý do từ chối chiến dịch (bắt buộc):', '');
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('Cần nhập lý do từ chối.');
      return;
    }
    try {
      setBusy(c.id);
      await adminReviewBrandCampaign(c.id, false, reason.trim());
      toast.success('Đã từ chối chiến dịch.');
      load('campaigns');
    } catch (e) {
      toast.error(errorMessage(e, 'Từ chối thất bại.'));
    } finally {
      setBusy(null);
    }
  };

  const openResolve = (deal: AdminBrandDeal, payCreator: boolean) => {
    setResolveModal({ deal, payCreator, note: '', totp: '' });
  };

  const confirmResolve = async () => {
    if (!resolveModal) return;
    const { deal, payCreator, note, totp } = resolveModal;
    if (totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số.');
      return;
    }
    try {
      setBusy(deal.id);
      await adminResolveBrandDeal(deal.id, totp, payCreator, note.trim() || undefined);
      toast.success(payCreator ? 'Đã trả creator.' : 'Đã hoàn doanh nghiệp.');
      setResolveModal(null);
      load('deals');
    } catch (e) {
      // 403 = mã Authenticator sai/thiếu (step-up bảo mật)
      toast.error(errorMessage(e, 'Mã Authenticator không hợp lệ hoặc xử lý thất bại.'));
    } finally {
      setBusy(null);
    }
  };

  const openCancelDecision = (request: AdminBrandCancelRequest, approve: boolean) => {
    setCancelModal({ request, approve, note: '', totp: '' });
  };

  const confirmCancelDecision = async () => {
    if (!cancelModal) return;
    const { request, approve, note, totp } = cancelModal;
    if (totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số.');
      return;
    }
    try {
      setBusy(request.id);
      if (approve) await adminApproveCancelRequest(request.id, totp);
      else await adminRejectCancelRequest(request.id, totp, note.trim() || undefined);
      const live = isLiveCancel(request);
      toast.success(
        approve
          ? live
            ? 'Đã đồng ý huỷ. Quảng cáo dừng ngay, creator được trả theo số giây đã phát hợp lệ.'
            : 'Đã đồng ý huỷ nhiệm vụ — tiền hoàn về ví doanh nghiệp.'
          : live
          ? 'Đã không đồng ý huỷ. Quảng cáo livestream chạy tiếp.'
          : 'Đã không đồng ý huỷ nhiệm vụ.'
      );
      setCancelModal(null);
      load('cancelRequests');
    } catch (e) {
      toast.error(errorMessage(e, 'Mã Authenticator không hợp lệ hoặc xử lý thất bại.'));
    } finally {
      setBusy(null);
    }
  };

  const tabCount = (k: string) =>
    k === 'liveReports' ? liveReportCount ?? 0 : k === 'liveCancels' ? liveCancelCount ?? 0 : 0;

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Hợp tác creator"
        description="Duyệt chiến dịch hợp tác giữa doanh nghiệp và creator, xử lý huỷ, gian lận và kháng cáo."
      />

      <AdminPillTabs
        ariaLabel="Lọc mục hợp tác creator"
        items={TABS.map((t) => ({ key: t.key as TabKey, label: t.label, count: tabCount(t.key) > 0 ? tabCount(t.key) : undefined }))}
        value={tab}
        onChange={setTab}
      />

      {tab === 'flaggedMessages' ? (
        <AdminFlaggedMessages />
      ) : tab === 'liveReports' ? (
        <AdminLiveReports onCount={setLiveReportCount} />
      ) : tab === 'liveCancels' ? (
        <AdminLiveCancelRequests onCount={setLiveCancelCount} />
      ) : loading ? (
        <AdminEmpty icon={<LoaderCircle size={22} className="adm-btn__spin" />}>Đang tải…</AdminEmpty>
      ) : tab === 'campaigns' ? (
        campaigns.length === 0 ? (
          <AdminEmpty>Không có chiến dịch nào chờ duyệt.</AdminEmpty>
        ) : (
          <div className="adm-stack">
            {campaigns.map((c) => (
              <div key={c.id} className="adm-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[color:var(--adm-heading)]">
                      {c.kind === 'Booking' ? 'Đơn đặt quảng cáo' : c.title}
                    </div>
                    {c.kind === 'Booking' && c.booking ? (
                      <AdminBookingReview booking={c.booking} ownerIdentityName={c.ownerIdentityName} />
                    ) : (
                    <>
                    <div className="text-xs adm-muted">
                      {ordererNoun(c.ordererKind)}: <AdminOrdererLabel name={c.brandName} kind={c.ordererKind} ownerIdentityName={c.ownerIdentityName} />
                      {(c.kind !== 'Proposal' || c.holdDays > 0) && <> · Giữ bài {c.holdDays} ngày</>}
                    </div>
                    {c.kind === 'Creative' || c.kind === 'Proposal' ? (
                      <div className="mt-1 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                        <span className="mr-2 adm-status adm-status--brand">
                          {c.kind === 'Proposal' ? 'Nhận đề xuất' : 'Brief sáng tạo'}
                        </span>
                        Mô tả: {c.briefText}
                      </div>
                    ) : (
                      <div className="mt-1 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{c.caption}</div>
                    )}
                    {c.destinationUrl && (
                      <div className="mt-1 text-xs">
                        Link đích:{' '}
                        <a href={safeHref(c.destinationUrl)} target="_blank" rel="noopener noreferrer" className="adm-link">
                          {c.destinationUrl}
                        </a>
                      </div>
                    )}
                    {/* Task 4: thông tin sàn (M2) — đa số campaign ở tab "Chờ duyệt" chưa đăng sàn
                        (chỉ đăng được sau khi Approved) nhưng vẫn hiện trạng thái cho rõ. */}
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Sàn: {listingStatusLabel(c.listingStatus)}
                      {c.listingStatus !== 'None' && c.slotsTotal != null && (
                        <>
                          {' '}
                          · Giá mỗi suất {fmtVnd(c.slotPriceVnd || 0)} · Suất {c.slotsUsed}/{c.slotsTotal} (còn{' '}
                          {slotsLeft(c.slotsTotal, c.slotsUsed)})
                        </>
                      )}
                    </div>
                    </>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      disabled={busy === c.id}
                      onClick={() => approveCampaign(c)}
                      className="adm-btn adm-btn--success adm-btn--32"
                    >
                      Duyệt
                    </button>
                    <button
                      disabled={busy === c.id}
                      onClick={() => rejectCampaign(c)}
                      className="adm-btn adm-btn--danger-soft adm-btn--32"
                    >
                      Từ chối
                    </button>
                    <button
                      onClick={() => openConversionsForCampaign(c.id)}
                      className="adm-btn adm-btn--outline adm-btn--32"
                    >
                      Xem chuyển đổi
                    </button>
                  </div>
                </div>
                {c.kind !== 'Booking' && (c.images.length > 0 || c.videos.length > 0) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {c.images.map((img) => (
                      <img key={img.id || img.url} src={img.url} alt="" className="size-20 rounded-md border border-gray-200 object-cover dark:border-gray-700" />
                    ))}
                    {c.videos.map((v) => (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <video key={v.id || v.url} src={v.url} controls className="h-20 rounded-md bg-black" />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : tab === 'deals' ? (
        deals.length === 0 ? (
          <AdminEmpty>Không có deal nào chờ xem xét.</AdminEmpty>
        ) : (
        <div className="adm-stack">
          {deals.map((d) => (
            <div key={d.id} className="adm-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-[color:var(--adm-heading)]">
                    {d.campaignTitle} <span className="text-sm font-normal text-gray-500">· <AdminOrdererLabel name={d.brandName} kind={d.ordererKind} ownerIdentityName={d.ownerIdentityName} /></span>
                  </div>
                  <div className="text-xs adm-muted">
                    Creator {d.creatorId.slice(0, 8)}… · Giá {fmtVnd(d.priceVnd)} · Creator nhận {fmtVnd(d.netVnd)} ·{' '}
                    Trạng thái {dealStatusLabel(d.status)} · Kết thúc {formatDate(d.endsAt)}
                  </div>
                  {d.closeReason && (
                    <div className="mt-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">Lý do: {d.closeReason}</div>
                  )}
                  {d.editedAfterReview && <EditedAfterReviewBadge />}
                  {d.postId && (
                    // M-9: bài NeedsReview thường đang Inactive (khoá) hoặc ẩn do báo cáo — trang chi tiết
                    // công khai (/share/post/{id}) không trả về gì cho trạng thái này (GetPostById chỉ
                    // đọc bài Public). Trỏ sang công cụ admin xử lý bài thay vì link công khai vỡ.
                    <a
                      href={d.closeReason?.includes('báo cáo') ? '/admin/post/reported' : '/admin/post/hidden'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="adm-link text-xs"
                    >
                      Mở công cụ xử lý bài (postId {d.postId.slice(0, 8)}…)
                    </a>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => openResolve(d, true)}
                    className="adm-btn adm-btn--success adm-btn--32"
                  >
                    Trả creator
                  </button>
                  <button
                    onClick={() => openResolve(d, false)}
                    className="adm-btn adm-btn--danger-soft adm-btn--32"
                  >
                    Hoàn doanh nghiệp
                  </button>
                  <button
                    onClick={() => openConversionsForCampaign(d.campaignId)}
                    className="adm-btn adm-btn--outline adm-btn--32"
                  >
                    Xem chuyển đổi
                  </button>
                  <button
                    onClick={() => setThreadDealId(d.id)}
                    className="adm-btn adm-btn--outline adm-btn--32"
                  >
                    Xem trao đổi
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        )
      ) : tab === 'cancelRequests' ? (
        cancelRequests.length === 0 ? (
        <AdminEmpty>Không có yêu cầu huỷ nào đang chờ.</AdminEmpty>
      ) : (
        <div className="adm-stack">
          {cancelRequests.map((r) => (
            <div key={r.id} className="adm-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-[color:var(--adm-heading)]">
                    {isLiveCancel(r) && (
                      <span className="mr-2 adm-status adm-status--brand">
                        Quảng cáo livestream
                      </span>
                    )}
                    {r.campaignTitle} <span className="text-sm font-normal text-gray-500">· <AdminOrdererLabel name={r.brandName} kind={r.ordererKind} ownerIdentityName={r.ownerIdentityName} /></span>
                  </div>
                  <div className="text-xs adm-muted">
                    Creator {r.creatorId.slice(0, 8)}… · Giá {fmtVnd(r.priceVnd)}
                    {isLiveCancel(r) ? (
                      <> · Đang phát quảng cáo trong các buổi live của creator</>
                    ) : (
                      <>
                        {' '}
                        · Trạng thái nhiệm vụ {taskStatusLabel(r.dealStatus)}
                        {r.reviewDueAt && <> · Hạn duyệt {formatDate(r.reviewDueAt)}</>}
                      </>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-gray-700 dark:text-gray-200">Lý do xin huỷ: {r.reason}</div>
                  {!isLiveCancel(r) && (
                    <button
                      type="button"
                      onClick={() => setInspectDealId(r.dealId)}
                      className="adm-link mt-1 text-xs"
                    >
                      Xem nhiệm vụ
                    </button>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => openCancelDecision(r, true)}
                    className="adm-btn adm-btn--success adm-btn--32"
                  >
                    Đồng ý huỷ
                  </button>
                  <button
                    onClick={() => openCancelDecision(r, false)}
                    className="adm-btn adm-btn--danger-soft adm-btn--32"
                  >
                    Không đồng ý
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        )
      ) : tab === 'disputes' ? (
        disputes.length === 0 ? (
          <AdminEmpty>Không có kháng cáo nào đang chờ.</AdminEmpty>
        ) : (
          <div className="adm-stack">
            {disputes.map((d) => (
              <div key={d.dispute.id} className="adm-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[color:var(--adm-heading)]">
                      {d.title} <span className="text-sm font-normal text-gray-500">· <AdminOrdererLabel name={d.businessName} kind={d.ordererKind} ownerIdentityName={d.ownerIdentityName} /></span>
                      {d.dispute.readyForDecision && (
                        <span className="ml-2 adm-status adm-status--warning">
                          Sẵn sàng quyết
                        </span>
                      )}
                    </div>
                    <div className="text-xs adm-muted">
                      Creator {d.creatorId.slice(0, 8)}… · Giá {fmtVnd(d.priceVnd)} · Gửi kháng cáo {formatDate(d.dispute.createdAt)}
                      {!d.dispute.businessRespondedAt && (
                        <> · Doanh nghiệp còn hạn giải trình: {countdownLabel(d.dispute.responseDueAt, nowMs)}</>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <button
                      type="button"
                      onClick={() => setReviewDisputeId(d.dispute.id)}
                      className="adm-btn adm-btn--primary adm-btn--32"
                    >
                      Xem xét
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : tab === 'sanctions' ? (
        <AdminSanctionPanel />
      ) : tab === 'blacklist' ? (
        <AdminBlacklistPanel />
      ) : tab === 'bannedAccounts' ? (
        <div className="adm-stack">
          {isSuperAdmin() && (
            <button
              type="button"
              onClick={() => setManualBanModal({ adAccountId: '', reason: '', totp: '' })}
              className="adm-btn adm-btn--danger-soft adm-btn--32"
            >
              Khoá thủ công
            </button>
          )}
          {bannedAccounts.length === 0 ? (
            <AdminEmpty>Không có tài khoản doanh nghiệp nào đang bị khoá.</AdminEmpty>
          ) : (
            <div className="adm-stack">
              {bannedAccounts.map((a) => (
                <div key={a.id} className="adm-card">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[color:var(--adm-heading)]"><AdminOrdererLabel name={a.businessName} kind={a.ordererKind} ownerIdentityName={a.ownerIdentityName} /></div>
                      <div className="text-xs adm-muted">Khoá ngày {formatDate(a.marketplaceBannedAt)}</div>
                      {a.marketplaceBanReason && (
                        <div className="mt-1 text-sm text-gray-700 dark:text-gray-200">Lý do: {a.marketplaceBanReason}</div>
                      )}
                      {a.marketplaceBanDisputeId ? (
                        <button
                          type="button"
                          onClick={() => setReviewDisputeId(a.marketplaceBanDisputeId as string)}
                          className="adm-link mt-1 text-xs"
                        >
                          Xem kháng cáo liên quan
                        </button>
                      ) : (
                        <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">Khoá thủ công (không qua kháng cáo)</div>
                      )}
                    </div>
                    {isSuperAdmin() && (
                      <div className="shrink-0">
                        <button
                          type="button"
                          onClick={() => setUnbanModal({ account: a, totp: '' })}
                          className="adm-btn adm-btn--success adm-btn--32"
                        >
                          Gỡ khoá
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : tab === 'reputations' ? (
        <div className="adm-stack">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={lowOnlyReputations}
              onChange={(e) => {
                const next = e.target.checked;
                setLowOnlyReputations(next);
                loadReputations(next);
              }}
            />
            Chỉ uy tín thấp
          </label>

          {reputations.length === 0 ? (
            <AdminEmpty>
              {lowOnlyReputations ? 'Không có hồ sơ nào đang uy tín thấp.' : 'Chưa có hồ sơ điểm uy tín nào.'}
            </AdminEmpty>
          ) : (
            <div className="adm-stack">
              {reputations.map((r) => (
                <div
                  key={`${r.subjectType}-${r.subjectId}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setReputationDetailSubject({ subjectType: r.subjectType, subjectId: r.subjectId })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setReputationDetailSubject({ subjectType: r.subjectType, subjectId: r.subjectId });
                  }}
                  className="adm-card cursor-pointer"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[color:var(--adm-heading)]">{subjectName(r)}</div>
                      <div className="text-xs adm-muted">
                        {subjectTypeLabel(r.subjectType)} · {avgStarsLabel(r.average)} · {r.ratingCount} lượt chấm
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {r.exempt && (
                          <span className="adm-status adm-status--brand">
                            Đã miễn trừ
                          </span>
                        )}
                        {r.isLowReputation && (
                          <span className="adm-status adm-status--danger">
                            Uy tín thấp
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      <button
                        type="button"
                        disabled={busy === `exempt-${r.subjectType}-${r.subjectId}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openExempt(r.subjectType, r.subjectId, r.exempt);
                        }}
                        className="adm-btn adm-btn--outline adm-btn--32"
                      >
                        {r.exempt ? 'Bỏ miễn trừ' : 'Miễn trừ'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : tab === 'conversions' ? (
        <div className="adm-stack">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[240px] flex-1">
              <label className="adm-label mb-1 block">Id chiến dịch</label>
              <input
                value={conversionCampaignId}
                onChange={(e) => setConversionCampaignId(e.target.value)}
                placeholder="VD: 3fa85f64-5717-4562-b3fc-2c963f66afa6"
                className="adm-input"
              />
            </div>
            <button
              type="button"
              onClick={() => loadConversions(conversionCampaignId, 1)}
              className="adm-btn adm-btn--primary adm-btn--36"
            >
              Xem chuyển đổi
            </button>
          </div>

          {!conversionsLoaded ? (
            <AdminEmpty>Nhập id chiến dịch để xem danh sách chuyển đổi.</AdminEmpty>
          ) : conversions.length === 0 ? (
            <AdminEmpty>Chiến dịch này chưa có chuyển đổi nào.</AdminEmpty>
          ) : (
            <>
              <div className="adm-stack">
                {conversions.map((c) => (
                  <div key={c.id} className="adm-card">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-[color:var(--adm-heading)]">
                          Mã đơn {c.orderId}
                          {c.valueVnd != null && <span className="text-sm font-normal text-gray-500"> · {fmtVnd(c.valueVnd)}</span>}
                        </div>
                        <div className="text-xs adm-muted">
                          Creator {c.creatorId.slice(0, 8)}… · Nguồn {c.source === 'Server' ? 'Máy chủ' : 'Trình duyệt'} ·{' '}
                          {formatDate(c.createdAt)}
                          {c.isTest && <span className="ml-1 font-semibold text-amber-600 dark:text-amber-400">· Lượt thử</span>}
                        </div>
                        {c.voidedAt && (
                          <div className="mt-1 text-sm font-semibold text-rose-600 dark:text-rose-400">
                            Đã huỷ {formatDate(c.voidedAt)}
                            {c.voidReason ? ` — ${c.voidReason}` : ''}
                          </div>
                        )}
                      </div>
                      {!c.voidedAt && (
                        <button
                          type="button"
                          onClick={() => setVoidModal({ conversion: c, reason: '', totp: '' })}
                          className="adm-btn adm-btn--danger-soft adm-btn--32 shrink-0"
                        >
                          Huỷ chuyển đổi
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {conversionsTotalPages > 1 && (
                <div className="flex items-center justify-center gap-3 text-sm">
                  <button
                    disabled={conversionsPage <= 1}
                    onClick={() => loadConversions(conversionCampaignId, conversionsPage - 1)}
                    className="adm-btn adm-btn--outline adm-btn--32"
                  >
                    Trước
                  </button>
                  <span className="adm-muted">
                    Trang {conversionsPage}/{conversionsTotalPages}
                  </span>
                  <button
                    disabled={conversionsPage >= conversionsTotalPages}
                    onClick={() => loadConversions(conversionCampaignId, conversionsPage + 1)}
                    className="adm-btn adm-btn--outline adm-btn--32"
                  >
                    Sau
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      {resolveModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setResolveModal(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl w-full max-w-[440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-1 text-lg font-semibold text-slate-800 dark:text-white">
              {resolveModal.payCreator ? 'Trả creator' : 'Hoàn doanh nghiệp'}
            </h4>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-300">
              Deal <b className="text-slate-700 dark:text-white">{resolveModal.deal.campaignTitle}</b> —{' '}
              {resolveModal.payCreator
                ? `creator sẽ nhận ${fmtVnd(resolveModal.deal.netVnd)}.`
                : 'doanh nghiệp sẽ được hoàn tiền đã ký quỹ.'}
            </p>
            <label className="adm-label mb-1 block">Ghi chú (tuỳ chọn)</label>
            <input
              value={resolveModal.note}
              onChange={(e) => setResolveModal((prev) => (prev ? { ...prev, note: e.target.value } : prev))}
              placeholder="VD: bài bị khoá do báo cáo sai"
              // M-1: BE ghép thêm tiền tố "Admin hoàn doanh nghiệp: " (25 ký tự) trước khi ghi vào cột
              // varchar(200) CloseReason — giới hạn FE thấp hơn 200 một khoảng an toàn để không bao giờ
              // chạm mép dù BE đã tự truncate lần cuối (M-1 phía payment).
              maxLength={170}
              className="adm-input mb-4"
            />
            <label className="adm-label mb-1 block">
              Mã Authenticator (6 số) <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <p className="mb-2 text-xs text-slate-400">Mở app Authenticator, nhập mã 6 số hiện tại để xác thực xử lý deal.</p>
            <input
              value={resolveModal.totp}
              onChange={(e) =>
                setResolveModal((prev) => (prev ? { ...prev, totp: e.target.value.replace(/\D/g, '').slice(0, 6) } : prev))
              }
              placeholder="123456"
              inputMode="numeric"
              className="adm-input mb-4 text-center text-lg tracking-[0.3em]"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setResolveModal(null)}
                className="adm-btn adm-btn--outline adm-btn--40"
              >
                Huỷ
              </button>
              <button
                disabled={resolveModal.totp.length !== 6 || busy === resolveModal.deal.id}
                onClick={confirmResolve}
                className="adm-btn adm-btn--success adm-btn--40"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setCancelModal(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl w-full max-w-[440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-1 text-lg font-semibold text-slate-800 dark:text-white">
              {isLiveCancel(cancelModal.request)
                ? cancelModal.approve
                  ? 'Đồng ý huỷ quảng cáo livestream'
                  : 'Không đồng ý huỷ quảng cáo livestream'
                : cancelModal.approve
                ? 'Đồng ý huỷ nhiệm vụ'
                : 'Không đồng ý huỷ nhiệm vụ'}
            </h4>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-300">
              {isLiveCancel(cancelModal.request) ? 'Quảng cáo livestream' : 'Brief'}{' '}
              <b className="text-slate-700 dark:text-white">{cancelModal.request.campaignTitle}</b> —{' '}
              {isLiveCancel(cancelModal.request)
                ? cancelModal.approve
                  ? 'quảng cáo dừng ngay, creator được trả theo số giây đã phát hợp lệ, phần còn lại hoàn cho doanh nghiệp.'
                  : 'quảng cáo chạy tiếp như cũ.'
                : cancelModal.approve
                ? 'nhiệm vụ sẽ huỷ, tiền hoàn về ví doanh nghiệp.'
                : 'nhiệm vụ chạy tiếp, doanh nghiệp vẫn phải xử lý bài nộp bình thường.'}
            </p>
            {!cancelModal.approve && (
              <>
                <label className="adm-label mb-1 block">Ghi chú (tuỳ chọn)</label>
                <input
                  value={cancelModal.note}
                  onChange={(e) => setCancelModal((prev) => (prev ? { ...prev, note: e.target.value } : prev))}
                  placeholder="VD: lý do chưa đủ thuyết phục"
                  maxLength={200}
                  className="adm-input mb-4"
                />
              </>
            )}
            <label className="adm-label mb-1 block">
              Mã Authenticator (6 số) <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <p className="mb-2 text-xs text-slate-400">Mở app Authenticator, nhập mã 6 số hiện tại để xác thực quyết định.</p>
            <input
              value={cancelModal.totp}
              onChange={(e) =>
                setCancelModal((prev) => (prev ? { ...prev, totp: e.target.value.replace(/\D/g, '').slice(0, 6) } : prev))
              }
              placeholder="123456"
              inputMode="numeric"
              className="adm-input mb-4 text-center text-lg tracking-[0.3em]"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCancelModal(null)}
                className="adm-btn adm-btn--outline adm-btn--40"
              >
                Huỷ
              </button>
              <button
                disabled={cancelModal.totp.length !== 6 || busy === cancelModal.request.id}
                onClick={confirmCancelDecision}
                className="adm-btn adm-btn--success adm-btn--40"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {inspectDealId && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setInspectDealId(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl max-h-[90vh] w-full max-w-[640px] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <BrandTaskReview dealId={inspectDealId} viewerRole="Admin" onBack={() => setInspectDealId(null)} />
            <div className="mt-4">
              <BrandThreadPanel dealId={inspectDealId} mode="admin" />
            </div>
          </div>
        </div>
      )}

      {threadDealId && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onClick={() => setThreadDealId(null)}>
          <div
            className="adm-card !gap-0 shadow-xl max-h-[90vh] w-full max-w-[640px] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <BrandThreadPanel dealId={threadDealId} mode="admin" showHeader />
          </div>
        </div>
      )}

      {/* M4 — màn xét 1 kháng cáo: 3 cột bằng chứng creator | giải trình doanh nghiệp | bằng chứng M3,
          xếp dọc trên di động (grid-cols-1). */}
      {reviewDisputeId && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setReviewDisputeId(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl max-h-[90vh] w-full max-w-[1100px] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className="text-lg font-semibold text-slate-800 dark:text-white">Xét kháng cáo</h4>
              <button
                type="button"
                onClick={() => setReviewDisputeId(null)}
                className="adm-btn adm-btn--outline adm-btn--32"
              >
                Đóng
              </button>
            </div>

            {disputeDetailLoading || !disputeDetail ? (
              <p className="adm-muted">Đang tải…</p>
            ) : (
              <>
                <div className="mb-4 text-sm text-slate-600 dark:text-slate-300">
                  {ordererNoun(disputeDetail.ordererKind)} <b className="text-slate-800 dark:text-white"><AdminOrdererLabel name={disputeDetail.businessName} kind={disputeDetail.ordererKind} ownerIdentityName={disputeDetail.ownerIdentityName} /></b>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="adm-card min-w-0">
                    <h5 className="mb-2 text-sm font-bold dark:text-white">Bằng chứng creator</h5>
                    <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">
                      {disputeDetail.dispute.description}
                    </p>
                    {disputeDetail.dispute.evidenceLinks.length > 0 && (
                      <div className="mt-2 flex flex-col gap-1">
                        {disputeDetail.dispute.evidenceLinks.map((l) => (
                          <a
                            key={l}
                            href={safeHref(l)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="adm-link truncate text-xs"
                          >
                            {l}
                          </a>
                        ))}
                      </div>
                    )}
                    {disputeDetail.dispute.evidenceImages.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {disputeDetail.dispute.evidenceImages.map((img) => (
                          <img
                            key={img.id || img.url}
                            src={img.url}
                            alt=""
                            className="size-20 rounded-md border border-gray-200 object-cover dark:border-gray-700"
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="adm-card min-w-0">
                    <h5 className="mb-2 text-sm font-bold dark:text-white">Giải trình doanh nghiệp</h5>
                    {disputeDetail.dispute.businessRespondedAt ? (
                      <>
                        {disputeDetail.dispute.businessResponse && (
                          <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">
                            {disputeDetail.dispute.businessResponse}
                          </p>
                        )}
                        {(disputeDetail.dispute.businessResponseLinks ?? []).map((l) => (
                          <a
                            key={l}
                            href={safeHref(l)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="adm-link mt-1 block truncate text-xs"
                          >
                            {l}
                          </a>
                        ))}
                      </>
                    ) : (
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                        Chưa giải trình — còn {countdownLabel(disputeDetail.dispute.responseDueAt, nowMs) || 'hết hạn'}
                      </p>
                    )}
                    {disputeDetail.dispute.decisionReason && (
                      <div className="adm-panel mt-3">
                        Đã quyết: {disputeDetail.dispute.decisionReason}
                      </div>
                    )}
                  </div>

                  <div className="adm-card min-w-0">
                    <h5 className="mb-2 px-2 pt-2 text-sm font-bold dark:text-white">Bằng chứng M3</h5>
                    <BrandTaskReview
                      dealId={disputeDetail.dispute.dealId}
                      viewerRole="Admin"
                      onBack={() => setReviewDisputeId(null)}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <BrandThreadPanel dealId={disputeDetail.dispute.dealId} mode="admin" showHeader />
                </div>

                <div className="mt-4 flex flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    disabled={!disputeDetail.dispute.readyForDecision}
                    onClick={() => setDecideOpen(true)}
                    className="adm-btn adm-btn--primary adm-btn--32"
                  >
                    Quyết kháng cáo
                  </button>
                </div>
                {!disputeDetail.dispute.readyForDecision && (
                  <p className="mt-2 text-right text-xs text-gray-400">
                    Chưa thể quyết: doanh nghiệp còn hạn giải trình.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {decideOpen && disputeDetail && (
        <AdminDisputeDecideDialog detail={disputeDetail} onClose={() => setDecideOpen(false)} onDecided={onDisputeDecided} />
      )}

      {sanctionAfterDispute && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
          <div className="adm-card !gap-0 shadow-xl max-h-[90vh] w-full max-w-[640px] overflow-y-auto">
            <h4 className="mb-1 text-lg font-semibold text-slate-800 dark:text-white">Xử phạt người đặt đơn</h4>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-300">
              Kháng cáo đã có kết quả. Phạt hay không là quyết định riêng; nếu phạt lỗi, kết quả kháng cáo vẫn giữ nguyên và bạn
              phạt lại ở tab Xử phạt người đặt đơn.
            </p>
            <AdminSanctionForm
              adAccountId={sanctionAfterDispute.adAccountId}
              disputeId={sanctionAfterDispute.disputeId}
              onDone={() => setSanctionAfterDispute(null)}
              onCancel={() => setSanctionAfterDispute(null)}
              cancelLabel="Bỏ qua"
            />
          </div>
        </div>
      )}
      {manualBanModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setManualBanModal(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl w-full max-w-[440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-4 text-lg font-semibold text-slate-800 dark:text-white">Khoá thủ công tài khoản doanh nghiệp</h4>
            <label className="adm-label mb-1 block">
              Id tài khoản doanh nghiệp <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <input
              value={manualBanModal.adAccountId}
              onChange={(e) => setManualBanModal((prev) => (prev ? { ...prev, adAccountId: e.target.value } : prev))}
              placeholder="VD: 3fa85f64-5717-4562-b3fc-2c963f66afa6"
              className="adm-input mb-4"
            />
            <label className="adm-label mb-1 block">
              Lý do ({DECISION_REASON_LIMITS.minLength}–{DECISION_REASON_LIMITS.maxLength} ký tự){' '}
              <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <textarea
              value={manualBanModal.reason}
              onChange={(e) => setManualBanModal((prev) => (prev ? { ...prev, reason: e.target.value } : prev))}
              maxLength={DECISION_REASON_LIMITS.maxLength}
              rows={3}
              className="adm-textarea mb-4"
            />
            <label className="adm-label mb-1 block">
              Mã Authenticator (6 số) <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <input
              value={manualBanModal.totp}
              onChange={(e) =>
                setManualBanModal((prev) => (prev ? { ...prev, totp: e.target.value.replace(/\D/g, '').slice(0, 6) } : prev))
              }
              placeholder="123456"
              inputMode="numeric"
              className="adm-input mb-4 text-center text-lg tracking-[0.3em]"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setManualBanModal(null)}
                className="adm-btn adm-btn--outline adm-btn--40"
              >
                Huỷ
              </button>
              <button
                disabled={
                  manualBanModal.totp.length !== 6 ||
                  !manualBanModal.adAccountId.trim() ||
                  !!validateDecisionReason(manualBanModal.reason) ||
                  busy === 'manual-ban'
                }
                onClick={confirmManualBan}
                className="adm-btn adm-btn--danger adm-btn--40"
              >
                Khoá
              </button>
            </div>
          </div>
        </div>
      )}

      {unbanModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setUnbanModal(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl w-full max-w-[440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-1 text-lg font-semibold text-slate-800 dark:text-white">Gỡ khoá tài khoản</h4>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-300">
              Tài khoản <b className="text-slate-700 dark:text-white">{unbanModal.account.businessName}</b> sẽ nhận lại được
              lời mời/đăng sàn hợp tác creator.
            </p>
            <label className="adm-label mb-1 block">
              Mã Authenticator (6 số) <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <input
              value={unbanModal.totp}
              onChange={(e) =>
                setUnbanModal((prev) => (prev ? { ...prev, totp: e.target.value.replace(/\D/g, '').slice(0, 6) } : prev))
              }
              placeholder="123456"
              inputMode="numeric"
              className="adm-input mb-4 text-center text-lg tracking-[0.3em]"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setUnbanModal(null)}
                className="adm-btn adm-btn--outline adm-btn--40"
              >
                Huỷ
              </button>
              <button
                disabled={unbanModal.totp.length !== 6 || busy === unbanModal.account.id}
                onClick={confirmUnban}
                className="adm-btn adm-btn--success adm-btn--40"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {/* M5 (Task 4) — chi tiết 1 hồ sơ điểm uy tín: mọi lượt chấm, kể cả chưa công bố (admin xem
          được hết — khác hồ sơ điểm công khai chỉ hiện lượt đã công bố). */}
      {reputationDetailSubject && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setReputationDetailSubject(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl max-h-[90vh] w-full max-w-[560px] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className="text-lg font-semibold text-slate-800 dark:text-white">Hồ sơ điểm uy tín</h4>
              <button
                type="button"
                onClick={() => setReputationDetailSubject(null)}
                className="adm-btn adm-btn--outline adm-btn--32"
              >
                Đóng
              </button>
            </div>

            {reputationDetailLoading || !reputationDetail ? (
              <p className="adm-muted">Đang tải…</p>
            ) : (
              <>
                <div className="adm-panel mb-3">
                  <div className="font-semibold text-[color:var(--adm-heading)]">{subjectName(reputationDetail)}</div>
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    {avgStarsLabel(reputationDetail.average)} · {reputationDetail.ratingCount} lượt chấm
                    {reputationDetail.isLowReputation && (
                      <span className="ml-2 adm-status adm-status--danger">
                        Uy tín thấp
                      </span>
                    )}
                    {reputationDetail.exempt && (
                      <span className="ml-2 adm-status adm-status--brand">
                        Đã miễn trừ
                      </span>
                    )}
                  </div>
                  {reputationDetail.exempt && reputationDetail.exemptReason && (
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Lý do miễn trừ: {reputationDetail.exemptReason}</div>
                  )}
                  <button
                    type="button"
                    disabled={busy === `exempt-${reputationDetail.subjectType}-${reputationDetail.subjectId}`}
                    onClick={() => openExempt(reputationDetail.subjectType, reputationDetail.subjectId, reputationDetail.exempt)}
                    className="adm-btn adm-btn--outline adm-btn--32 mt-2"
                  >
                    {reputationDetail.exempt ? 'Bỏ miễn trừ' : 'Miễn trừ'}
                  </button>
                </div>

                {reputationDetail.ratings.length === 0 ? (
                  <p className="adm-muted">Chưa có lượt chấm nào.</p>
                ) : (
                  <div className="adm-stack">
                    {reputationDetail.ratings.map((rt) => (
                      <div key={rt.id} className="adm-panel">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="font-semibold text-[color:var(--adm-heading)]">
                            {rt.raterRole === 'Creator' ? 'Creator' : 'Doanh nghiệp'} chấm {rt.stars} ★
                          </span>
                          {!rt.revealedAt && (
                            <span className="adm-status adm-status--warning">
                              Chưa công bố
                            </span>
                          )}
                        </div>
                        {rt.comment && <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">{rt.comment}</p>}
                        {rt.hiddenAt && (
                          <div className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                            Đã ẩn{rt.hiddenReason ? `. Lý do: ${rt.hiddenReason}` : ''}
                          </div>
                        )}
                        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                          <span className="text-xs text-gray-400 dark:text-gray-500">{formatDate(rt.createdAt)}</span>
                          <button
                            type="button"
                            onClick={() => setHideTarget({ ratingId: rt.id, mode: rt.hiddenAt ? 'unhide' : 'hide' })}
                            className="adm-btn adm-btn--outline adm-btn--32"
                          >
                            {rt.hiddenAt ? 'Bỏ ẩn' : 'Ẩn đánh giá'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {hideTarget && (
        <AdminRatingHideDialog
          ratingId={hideTarget.ratingId}
          mode={hideTarget.mode}
          onClose={() => setHideTarget(null)}
          onDone={() => {
            setHideTarget(null);
            loadReputations(lowOnlyReputations);
            if (reputationDetailSubject) {
              adminGetReputation(reputationDetailSubject.subjectType, reputationDetailSubject.subjectId)
                .then(setReputationDetail)
                .catch(() => undefined);
            }
          }}
        />
      )}
      {/* M5 (Task 4) — đặt/bỏ miễn trừ: lý do 10–500 ký tự + mã Authenticator, cùng mẫu `decideModal`. */}
      {exemptModal && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setExemptModal(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl w-full max-w-[440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-4 text-lg font-semibold text-slate-800 dark:text-white">
              {exemptModal.exempt ? 'Miễn trừ' : 'Bỏ miễn trừ'}
            </h4>
            <label className="adm-label mb-1 block">
              Lý do ({RATING_EXEMPT_REASON_LIMITS.minLength}–{RATING_EXEMPT_REASON_LIMITS.maxLength} ký tự){' '}
              <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <textarea
              value={exemptModal.reason}
              onChange={(e) => setExemptModal((prev) => (prev ? { ...prev, reason: e.target.value } : prev))}
              maxLength={RATING_EXEMPT_REASON_LIMITS.maxLength}
              rows={4}
              placeholder="VD: điểm thấp do khách quan, đã xác minh không phải lỗi của tài khoản."
              className="adm-textarea mb-1"
            />
            <div className="mb-4 text-right text-xs text-slate-400">
              {exemptModal.reason.trim().length}/{RATING_EXEMPT_REASON_LIMITS.maxLength}
            </div>
            <label className="adm-label mb-1 block">
              Mã Authenticator (6 số) <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <input
              value={exemptModal.totp}
              onChange={(e) =>
                setExemptModal((prev) => (prev ? { ...prev, totp: e.target.value.replace(/\D/g, '').slice(0, 6) } : prev))
              }
              placeholder="123456"
              inputMode="numeric"
              className="adm-input mb-4 text-center text-lg tracking-[0.3em]"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setExemptModal(null)}
                className="adm-btn adm-btn--outline adm-btn--40"
              >
                Huỷ
              </button>
              <button
                disabled={
                  exemptModal.totp.length !== 6 ||
                  !!validateRatingExemptReason(exemptModal.reason) ||
                  busy === `exempt-${exemptModal.subjectType}-${exemptModal.subjectId}`
                }
                onClick={confirmExempt}
                className="adm-btn adm-btn--success adm-btn--40"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Đo chuyển đổi — huỷ 1 chuyển đổi gian lận: lý do 10–500 ký tự + mã Authenticator, cùng mẫu `exemptModal`. */}
      {voidModal && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setVoidModal(null)}
        >
          <div
            className="adm-card !gap-0 shadow-xl w-full max-w-[440px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-1 text-lg font-semibold text-slate-800 dark:text-white">Huỷ chuyển đổi</h4>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-300">
              Mã đơn <b className="text-slate-700 dark:text-white">{voidModal.conversion.orderId}</b> sẽ bị loại khỏi
              báo cáo ngay sau khi huỷ.
            </p>
            <label className="adm-label mb-1 block">
              Lý do (10–500 ký tự) <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <textarea
              value={voidModal.reason}
              onChange={(e) => setVoidModal((prev) => (prev ? { ...prev, reason: e.target.value } : prev))}
              maxLength={500}
              rows={4}
              placeholder="VD: phát hiện đơn hàng giả, lượt bấm từ bot."
              className="adm-textarea mb-1"
            />
            <div className="mb-4 text-right text-xs text-slate-400">{voidModal.reason.trim().length}/500</div>
            <label className="adm-label mb-1 block">
              Mã Authenticator (6 số) <span className="text-[color:var(--adm-danger)]">*</span>
            </label>
            <input
              value={voidModal.totp}
              onChange={(e) =>
                setVoidModal((prev) => (prev ? { ...prev, totp: e.target.value.replace(/\D/g, '').slice(0, 6) } : prev))
              }
              placeholder="123456"
              inputMode="numeric"
              className="adm-input mb-4 text-center text-lg tracking-[0.3em]"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setVoidModal(null)}
                className="adm-btn adm-btn--outline adm-btn--40"
              >
                Huỷ
              </button>
              <button
                disabled={
                  voidModal.totp.length !== 6 ||
                  voidModal.reason.trim().length < 10 ||
                  voidModal.reason.trim().length > 500 ||
                  busy === voidModal.conversion.id
                }
                onClick={confirmVoidConversion}
                className="adm-btn adm-btn--danger adm-btn--40"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ManagementBrandDeals: React.FC = () =>
  hasStrictPermission(PERMISSION.AdsManage) ? <ManagementBrandDealsInner /> : <NoAccess />;

export default ManagementBrandDeals;
